import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { UrlService } from '../../../../environments/urlService';
import { Company, CompanyForKpiDTO } from '../dto/Company';
import { Observable } from 'rxjs';
import { CompanyCreateDto } from '../component/shared/crud/dto/CompanyCreateDto';
import { IS_SPINNER_ACTIVE } from '../../../util/interceptors';
import {
  CompanyDto,
  CompanyMiniDto,
  CompanySearchDto,
} from '../../recovery/component/recovery-company/dto/CompanyWithOrgDto';
import { CompanyWithRoundsDTO } from '../../company/dto/CompanyWithRoundsDTO';
import { CompanyWithMetricsDTO } from '../../company/dto/CompanyWithMetricsDTO';
import {
  CompaniesSelectorDto,
} from '../component/shared/company-with-logo-description/company-with-logo-description.component';
import { CompanyForOrgOverviewDTO } from '../../company/dto/CompanyForOrgOverviewDTO';
import {
  CompanyStageForBackEnd,
  CompanyIndustryAndDescUpdateDto,
  ResponsibleManagerForBackEnd,
} from '../../company/dto/CompanyAuxiliary';

@Injectable({
  providedIn: 'root',
})
export class CompanyBackendService {
  private readonly url: string;

  constructor(private http: HttpClient, private urlService: UrlService) {
    this.url = this.urlService.getFmsUrl() + 'companies/';
  }

  getByUUID(uuid: string): Observable<Company> {
    return this.http.get<Company>(
      this.url, { params: { uuid }, withCredentials: true },
    );
  }

  getByUUIDWithMembers(uuid: string): Observable<Company> {
    return this.http.get<Company>(
      this.url + 'single', { params: { uuid }, withCredentials: true },
    );
  }

  getWithMetricsByOrg(orgUUID: string): Observable<CompanyWithMetricsDTO[]> {
    return this.http.get<CompanyWithMetricsDTO[]>(
      this.url + 'organization/' + orgUUID, { withCredentials: true },
    );
  }

  getCompaniesByFundIdAndPeriodWithStatus(
    fundUUID: string,
    companyUUID: string,
    period: string,
  ): Observable<CompaniesSelectorDto> {
    return this.http.get<CompaniesSelectorDto>(
      this.url + 'by-fund-period-with-status', {
        params: {
          fundUUID, companyUUID, period,
        }, withCredentials: true,
      },
    );
  }

  getByFundUUID(fundUUID: string): Observable<CompanyMiniDto[]> {
    return this.http.get<CompanyMiniDto[]>(
      this.url + 'fund/mini', {
        params: {
          fundUUID,
        }, withCredentials: true,
      },
    );
  }

  update(company: CompanyCreateDto): Promise<Company> {
    return this.http
      .put<Company>(
      this.url, company,
      {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      }).toPromise();
  }

  updatePartial(dto: CompanyIndustryAndDescUpdateDto): Observable<void> {
    return this.http.patch<void>(this.url + 'partial-update', dto, {
      withCredentials: true,
    });
  }

  updateWithRounds(company: CompanyCreateDto): Promise<CompanyWithRoundsDTO> {
    return this.http
      .put<CompanyWithRoundsDTO>(
      this.url + 'with-rounds', company,
      {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      }).toPromise();
  }

  updateCompanyStage(body: CompanyStageForBackEnd): Promise<void> {
    return this.http
      .patch<void>(
      this.url + 'stage', body,
      {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      }).toPromise();
  }

  updateResponsibleManager(body: ResponsibleManagerForBackEnd): Promise<void> {
    return this.http
      .patch<void>(
      this.url + 'responsible-manager', body,
      {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      }).toPromise();
  }

  create(company: CompanyCreateDto, orgUUID: string): Promise<Company> {
    return this.http.post<Company>(this.url, company,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        params: { orgUUID },
        withCredentials: true,
      })
      .toPromise();
  }

  getByNameSubstring(name: string): Observable<CompanySearchDto[]> {
    return this.http
      .get<CompanySearchDto[]>(
      this.url + 'find/pp',
      {
        params: {
          name,
        }, withCredentials: true,
      },
    );
  }

  getMiniByNameSubstring(name: string): Promise<CompanyMiniDto[]> {
    return this.http
      .get<CompanyMiniDto[]>(
      this.url + 'find',
      {
        params: {
          name,
        }, withCredentials: true,
      },
    ).toPromise();
  }

  getMini(orgUUID: string): Promise<CompanyMiniDto[]> {
    return this.http
      .get<CompanyMiniDto[]>(
      this.url + 'organization/mini',
      {
        params: {
          orgUUID,
        }, withCredentials: true,
      },
    ).toPromise();
  }

  getById(id: number): Promise<CompanyDto> {
    return this.http
      .get<CompanyDto>(
      this.url + id,
      {
        withCredentials: true,
      },
    ).toPromise();
  }

  getForOrgOverview(orgUUID: string): Observable<CompanyForOrgOverviewDTO[]> {
    return this.http.get<CompanyForOrgOverviewDTO[]>(this.url + 'organization', {
      params: { orgUUID }, context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  removeCompanyById(companyUuid: string): Observable<void> {
    return this.http.delete<void>(this.url + 'company/' + companyUuid, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  bulkCompaniesDelete(companyUUIDs: string[]): Observable<void> {
    return this.http.delete<void>(this.url + 'selected-companies', {
      params: { companyUUIDs },
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getSectorsFromServer(): Observable<string[]> {
    return this.http.get<string[]>(this.url + 'sectors', {
      withCredentials: true,
    });
  }

  getByOrgWithEmployees(orgUUID: string): Observable<CompanyForKpiDTO[]> {
    return this.http.get<CompanyForKpiDTO[]>(this.url + 'kpi', {
      params: {
        orgUUID,
      },
      withCredentials: true,
    });
  }

  isCompanyDeletable(companyUUID: string): Observable<boolean> {
    return this.http.get<boolean>(this.url + companyUUID + '/is-deletable', {
      withCredentials: true,
    });
  }

  getByOrg(orgUUID: string): Observable<Company[]> {
    return this.http.get<Company[]>(this.url + 'by-org', {
      params: { orgUUID },
      withCredentials: true,
    });
  }
}
